import './Editor.css'
import { useEffect } from 'react'
import { cn } from 'components/UI/cn'
import {
  CommonEditorProps,
  EditorProps,
  useEditorCore,
  EditorToolbar,
} from './EditorCore'

export function Editor(props: EditorProps & CommonEditorProps) {
  const {
    label,
    optional,
    className,
    borderless,
    disabled,
    noMargin,
    error,
    disableToolbar,
    additionalToolbarButtons,
    toolbarPrefix,
    toolbarSuffix,
    toolbarClassName,
  } = props

  const { uniqueId, isFocused, hasValue, quillEditor } = useEditorCore(props)

  // StyledEditor-specific effect for handling the ql-blank class
  useEffect(() => {
    if (!quillEditor) return

    if (isFocused && !hasValue) {
      quillEditor.root.classList.add('ql-blank')
    } else {
      quillEditor.root.classList.remove('ql-blank')
    }
  }, [quillEditor, isFocused, hasValue])

  return (
    <div className={cn('relative', noMargin ? 'mt-0' : 'mt-2', '!mb-0')}>
      {label && (
        <label
          className={cn(
            'absolute transition-all duration-200 z-10',
            isFocused || hasValue
              ? '-top-2 text-xs px-1 bg-gradient-to-t from-white from-50% to-transparent to-50%'
              : 'top-3 pointer-events-none text-sm font-medium leading-none',
            'left-2.5'
          )}
        >
          {label}
          {optional && (
            <span className={cn('text-sm italic font-normal')}>
              {' '}
              - optional
            </span>
          )}
        </label>
      )}
      <div
        className={cn(
          'relative',
          borderless
            ? 'borderless-editor'
            : 'border border-input rounded-md overflow-hidden',
          error?.message && 'border-red-500',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
      >
        <div
          className={cn(
            className,
            // Override Quill default styles with Tailwind-like classes
            '[&_.ql-container]:border-none',
            '[&_.ql-editor]:py-2 [&_.ql-editor]:px-2',
            '[&_.ql-editor]:overflow-auto', // Ensure overflow is handled inside the editor
            '[&_.ql-editor]:max-h-[inherit]', // Inherit max height from parent
            // Styling placeholder to match label
            '[&_.ql-editor.ql-blank::before]:font-normal',
            '[&_.ql-editor.ql-blank::before]:text-xs', // Ensure text-xs matches label
            '[&_.ql-editor.ql-blank::before]:text-gray-500',
            '[&_.ql-editor.ql-blank::before]:not-italic',
            '[&_.ql-editor.ql-blank::before]:top-3',
            '[&_.ql-editor.ql-blank::before]:left-2.5',
            '[&_.ql-editor.ql-blank::before]:pointer-events-none'
          )}
          id={`editor-${uniqueId}`}
        ></div>
        <div
          className={cn(
            toolbarPrefix || toolbarSuffix
              ? [
                  'flex items-center gap-2 pl-2 pr-3 py-2',
                  // Strip Quill toolbar chrome so it sits inline with prefix/suffix
                  '[&_.ql-toolbar]:!border-0 [&_.ql-toolbar]:!rounded-none [&_.ql-toolbar]:!p-0 [&_.ql-toolbar]:!m-0',
                  // Keep toolbar buttons on one line
                  '[&_.ql-toolbar]:!flex [&_.ql-toolbar]:!items-center [&_.ql-toolbar]:!flex-nowrap',
                ]
              : undefined,
            toolbarClassName
          )}
          style={{ display: disableToolbar ? 'none' : undefined }}
        >
          {toolbarPrefix}
          <EditorToolbar
            uniqueId={uniqueId}
            disableToolbar={disableToolbar}
            additionalToolbarButtons={additionalToolbarButtons}
          />
          {toolbarSuffix && (
            <div className="ml-auto flex items-center">{toolbarSuffix}</div>
          )}
        </div>
      </div>
      {error?.message && (
        <p className="mt-1 text-red-500 text-sm">{error.message}</p>
      )}
    </div>
  )
}
